import React, { useState, useEffect, useRef } from 'react';
import { Card, Button, Badge, ProgressBar } from 'react-bootstrap';
import { Play, Pause, Square, Clock } from 'react-bootstrap-icons';

const PomodoroTimer = ({ activeTask, onTimerComplete }) => {
    const [timeLeft, setTimeLeft] = useState(25 * 60); // 25 minutes in seconds
    const [isRunning, setIsRunning] = useState(false);
    const [isBreak, setIsBreak] = useState(false);
    const [completedPomodoros, setCompletedPomodoros] = useState(0);
    const intervalRef = useRef(null);

    useEffect(() => {
        if (activeTask) {
            // Always restart timer when a new task is selected
            setIsRunning(true);
            setTimeLeft(25 * 60); // Reset to 25 minutes
            setIsBreak(false);
        }
    }, [activeTask]);

    useEffect(() => {
        if (isRunning && timeLeft > 0) {
            intervalRef.current = setInterval(() => {
                setTimeLeft(prev => prev - 1);
            }, 1000);
        } else {
            clearInterval(intervalRef.current);
        }

        return () => clearInterval(intervalRef.current);
    }, [isRunning, timeLeft]);

    useEffect(() => {
        if (timeLeft === 0) {
            handleTimerComplete();
        }
    }, [timeLeft]);

    const handleTimerComplete = () => {
        setIsRunning(false);
        
        if (!isBreak) {
            // Work session completed
            setCompletedPomodoros(prev => prev + 1);
            const newCount = completedPomodoros + 1;
            
            // Determine break length
            const isLongBreak = newCount % 4 === 0;
            const breakTime = isLongBreak ? 15 * 60 : 5 * 60; // 15 min or 5 min
            
            setTimeLeft(breakTime);
            setIsBreak(true);
            
            // Notify parent component
            if (onTimerComplete) {
                onTimerComplete(activeTask, 'work_completed');
            }
            
            // Browser notification
            if (Notification.permission === 'granted') {
                new Notification('Pomodoro Complete!', {
                    body: `Time for a ${isLongBreak ? '15-minute' : '5-minute'} break!`,
                    icon: '/favicon.ico'
                });
            }
        } else {
            // Break completed
            setTimeLeft(25 * 60); // Reset to 25 minutes
            setIsBreak(false);
            
            if (onTimerComplete) {
                onTimerComplete(activeTask, 'break_completed');
            }
            
            if (Notification.permission === 'granted') {
                new Notification('Break Over!', {
                    body: 'Ready for another focused work session?',
                    icon: '/favicon.ico'
                });
            }
        }
    };

    const startTimer = () => {
        // Request notification permission
        if (Notification.permission === 'default') {
            Notification.requestPermission();
        }
        setIsRunning(true);
    };

    const pauseTimer = () => {
        setIsRunning(false);
    };

    const resetTimer = () => {
        setIsRunning(false);
        setTimeLeft(isBreak ? (completedPomodoros % 4 === 0 ? 15 * 60 : 5 * 60) : 25 * 60);
    };

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    const getProgress = () => {
        const totalTime = isBreak ? (completedPomodoros % 4 === 0 ? 15 * 60 : 5 * 60) : 25 * 60;
        return ((totalTime - timeLeft) / totalTime) * 100;
    };

    return (
        <Card className="pomodoro-timer">
            <Card.Header className={`text-white ${isBreak ? 'bg-success' : 'bg-primary'}`}>
                <div className="d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">
                        <Clock className="me-2" />
                        {isBreak ? 'Break Time' : 'Focus Time'}
                    </h6>
                    <Badge bg="light" text="dark">
                        {completedPomodoros} completed
                    </Badge>
                </div>
            </Card.Header>
            <Card.Body className="text-center">
                {activeTask && (
                    <div className="mb-3">
                        <small className="text-muted">Working on:</small>
                        <div className="fw-bold">{activeTask.title}</div>
                    </div>
                )}
                
                <div className="mb-3">
                    <h2 className="display-4 mb-2">{formatTime(timeLeft)}</h2>
                    <ProgressBar 
                        now={getProgress()} 
                        variant={isBreak ? 'success' : 'primary'}
                        className="mb-3"
                    />
                </div>

                <div className="d-flex justify-content-center gap-2">
                    {!isRunning ? (
                        <Button variant="success" onClick={startTimer}>
                            <Play className="me-1" /> Start
                        </Button>
                    ) : (
                        <Button variant="warning" onClick={pauseTimer}>
                            <Pause className="me-1" /> Pause
                        </Button>
                    )}
                    <Button variant="secondary" onClick={resetTimer}>
                        <Square className="me-1" /> Reset
                    </Button>
                </div>
            </Card.Body>
        </Card>
    );
};

export default PomodoroTimer;
